import { subscriptionApi as api } from './subscriptionApi';
export const addTagTypes = [
  'Payments',
  'StripeSubscriptions',
  'Stripe Subscriptions',
  'SubscriptionFeatures',
  'SubscriptionPlans',
  'UserSubscriptions'
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: build => ({
      getPayments: build.query<GetPaymentsApiResponse, GetPaymentsApiArg>({
        query: queryArg => ({
          url: `/payments`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId
          }
        }),
        providesTags: ['Payments']
      }),
      stripePaymentCheckout: build.mutation<
        StripePaymentCheckoutApiResponse,
        StripePaymentCheckoutApiArg
      >({
        query: queryArg => ({
          url: `/stripePaymentCheckout`,
          method: 'POST',
          body: queryArg.stripeCreateCheckOut
        }),
        invalidatesTags: ['StripeSubscriptions']
      }),
      createStripeCustomer: build.mutation<
        CreateStripeCustomerApiResponse,
        CreateStripeCustomerApiArg
      >({
        query: queryArg => ({
          url: `/stripeCustomerCreate`,
          method: 'POST',
          body: queryArg.stripeCreateCustomer
        }),
        invalidatesTags: ['StripeSubscriptions']
      }),
      createBillingPortalSession: build.mutation<
        CreateBillingPortalSessionApiResponse,
        CreateBillingPortalSessionApiArg
      >({
        query: queryArg => ({
          url: `/billing-portal`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['StripeSubscriptions']
      }),
      cancelSubscription: build.mutation<
        CancelSubscriptionApiResponse,
        CancelSubscriptionApiArg
      >({
        query: () => ({ url: `/cancel-subscription`, method: 'POST' }),
        invalidatesTags: ['StripeSubscriptions']
      }),
      cancelCancellation: build.mutation<
        CancelCancellationApiResponse,
        CancelCancellationApiArg
      >({
        query: () => ({ url: `/cancel-cancellation`, method: 'POST' }),
        invalidatesTags: ['StripeSubscriptions']
      }),
      downgradePlan: build.mutation<
        DowngradePlanApiResponse,
        DowngradePlanApiArg
      >({
        query: queryArg => ({
          url: `/downgrade-plan`,
          method: 'POST',
          body: queryArg.downgradePlanRequest
        }),
        invalidatesTags: ['StripeSubscriptions']
      }),
      cancelScheduledDowngrade: build.mutation<
        CancelScheduledDowngradeApiResponse,
        CancelScheduledDowngradeApiArg
      >({
        query: () => ({ url: `/cancel-downgrade`, method: 'POST' }),
        invalidatesTags: ['StripeSubscriptions']
      }),
      getPlanChangeOptions: build.query<
        GetPlanChangeOptionsApiResponse,
        GetPlanChangeOptionsApiArg
      >({
        query: () => ({ url: `/plan-options` }),
        providesTags: ['StripeSubscriptions']
      }),
      calculateUpgradeCost: build.mutation<
        CalculateUpgradeCostApiResponse,
        CalculateUpgradeCostApiArg
      >({
        query: queryArg => ({
          url: `/calculate-upgrade`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['Stripe Subscriptions']
      }),
      createUpgradeCheckout: build.mutation<
        CreateUpgradeCheckoutApiResponse,
        CreateUpgradeCheckoutApiArg
      >({
        query: queryArg => ({
          url: `/upgrade-checkout`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['Stripe Subscriptions']
      }),
      reactivateCanceledSubscription: build.mutation<
        ReactivateCanceledSubscriptionApiResponse,
        ReactivateCanceledSubscriptionApiArg
      >({
        query: () => ({ url: `/reactivate-subscription`, method: 'POST' }),
        invalidatesTags: ['StripeSubscriptions']
      }),
      getSubscriptionFeaturesById: build.query<
        GetSubscriptionFeaturesByIdApiResponse,
        GetSubscriptionFeaturesByIdApiArg
      >({
        query: queryArg => ({
          url: `/subscriptionFeatures?subscriptionPlanId=${queryArg.id}`
        }),
        providesTags: ['SubscriptionFeatures']
      }),
      getSubscriptionPlans: build.query<
        GetSubscriptionPlansApiResponse,
        GetSubscriptionPlansApiArg
      >({
        query: queryArg => ({
          url: `/subscriptionPlans`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId
          }
        }),
        providesTags: ['SubscriptionPlans']
      }),
      getUserSubscriptionPlans: build.query<
        GetUserSubscriptionPlansApiResponse,
        GetUserSubscriptionPlansApiArg
      >({
        query: () => ({ url: `/UserSubscriptionPlans` }),
        providesTags: ['SubscriptionPlans']
      }),
      getUserSubscriptions: build.query<
        GetUserSubscriptionsApiResponse,
        GetUserSubscriptionsApiArg
      >({
        query: queryArg => ({
          url: `/userSubscriptions`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId
          }
        }),
        providesTags: ['UserSubscriptions']
      })
    }),
    overrideExisting: false
  });
export { injectedRtkApi as enhancedApi };
export type GetPaymentsApiResponse = /** status 200 Payments found. */ {
  ok?: boolean;
  data?: Payments;
  message?: string;
};
export type GetPaymentsApiArg = {
  /** Search text */
  search?: string;
  /** offset for pagination */
  offset?: number;
  /** number of rows to fetch */
  limit?: number;
  /** sort */
  sortby?: string;
  /** order */
  orderby?: string;
  /** userId */
  userId?: string;
};
export type StripePaymentCheckoutApiResponse =
  /** status 200 StripeSubscriptions found. */ {
    ok?: boolean;
    data?: StripeSubscriptions;
    message?: string;
  };
export type StripePaymentCheckoutApiArg = {
  /** Permissions that needs to be added to the application */
  stripeCreateCheckOut: StripeCreateCheckOut;
};
export type CreateStripeCustomerApiResponse =
  /** status 201 Payments has been created successfully. */ {
    ok?: boolean;
    data?: StripeCreateCustomer;
    message?: string;
  };
export type CreateStripeCustomerApiArg = {
  /** Permissions that needs to be added to the application */
  stripeCreateCustomer: StripeCreateCustomer;
};
export type CreateBillingPortalSessionApiResponse =
  /** status 200 Billing portal session created successfully. */ {
    ok?: boolean;
    data?: {
      url?: string;
    };
    message?: string;
  };
export type CreateBillingPortalSessionApiArg = {
  /** Optional return URL */
  body: {
    type?: string;
    properties?: {
      returnUrl?: {
        type?: string;
        example?: string;
      };
    };
  };
};
export type CancelSubscriptionApiResponse =
  /** status 200 Subscription cancellation scheduled successfully. */ CancelSubscriptionResponse;
export type CancelSubscriptionApiArg = void;
export type CancelCancellationApiResponse =
  /** status 200 Subscription reactivated successfully. */ CancelCancellationResponse;
export type CancelCancellationApiArg = void;
export type DowngradePlanApiResponse =
  /** status 200 Downgrade scheduled successfully. */ DowngradePlanResponse;
export type DowngradePlanApiArg = {
  /** New pricing plan ID (must be lower price than current plan) */
  downgradePlanRequest: DowngradePlanRequest;
};
export type CancelScheduledDowngradeApiResponse =
  /** status 200 Scheduled downgrade canceled successfully. */ {
    ok?: boolean;
    data?: {
      success?: boolean;
      message?: string;
    };
    message?: string;
  };
export type CancelScheduledDowngradeApiArg = void;
export type GetPlanChangeOptionsApiResponse =
  /** status 200 Plan options retrieved successfully. */ PlanOptionsResponse;
export type GetPlanChangeOptionsApiArg = void;
export type CalculateUpgradeCostApiResponse =
  /** status 200 Upgrade cost calculated successfully. */ {
    ok?: boolean;
    data?: {
      success?: boolean;
      calculation?: {
        oldPlan?: string;
        newPlan?: string;
        oldPrice?: number;
        newPrice?: number;
        proratedCharge?: number;
        daysRemaining?: number;
        totalDays?: number;
        nextBillingAmount?: number;
        nextBillingDate?: string;
      };
    };
    message?: string;
  };
export type CalculateUpgradeCostApiArg = {
  /** New pricing plan ID to calculate upgrade cost */
  body: {
    newPricingPlanId?: string;
  };
};
export type CreateUpgradeCheckoutApiResponse =
  /** status 200 Checkout session created successfully. Redirect user to sessionUrl. */ {
    ok?: boolean;
    data?: {
      success?: boolean;
      /** Stripe Checkout URL - redirect user to this URL */
      sessionUrl?: string;
      /** Stripe Checkout Session ID */
      sessionId?: string;
    };
    message?: string;
  };
export type CreateUpgradeCheckoutApiArg = {
  /** Upgrade checkout details */
  body: {
    newPricingPlanId?: string;
  };
};
export type ReactivateCanceledSubscriptionApiResponse =
  /** status 200 Subscription reactivated successfully. */ {
    ok?: boolean;
    data?: {
      success?: boolean;
      message?: string;
      details?: {
        planName?: string;
        status?: 'active' | 'trialing';
        billingDate?: string;
        amount?: number;
        currencyCode?: string;
      };
    };
    message?: string;
  };
export type ReactivateCanceledSubscriptionApiArg = {
  body: {
    newPricingPlanId?: string;
  };
};
export type GetSubscriptionPlansApiResponse =
  /** status 200 SubscriptionPlans found. */ {
    ok?: boolean;
    data?: SubscriptionPlans;
    message?: string;
  };
export type GetSubscriptionPlansApiArg = {
  /** Search text */
  search?: string;
  /** offset for pagination */
  offset?: number;
  /** number of rows to fetch */
  limit?: number;
  /** sort */
  sortby?: string;
  /** order */
  orderby?: string;
  /** userId */
  userId?: string;
};
export type GetUserSubscriptionPlansApiResponse =
  /** status 200 SubscriptionPlans found. */ {
    ok?: boolean;
    data?: UserSubscriptionPlan;
    message?: string;
  };
export type GetUserSubscriptionPlansApiArg = void;

export type GetUserSubscriptionsApiResponse =
  /** status 200 UserSubscriptions found. */ {
    ok?: boolean;
    data?: UserSubscriptions;
    message?: string;
  };
export type GetUserSubscriptionsApiArg = {
  /** Search text */
  search?: string;
  /** offset for pagination */
  offset?: number;
  /** number of rows to fetch */
  limit?: number;
  /** sort */
  sortby?: string;
  /** order */
  orderby?: string;
  /** userId */
  userId?: string;
};
export type Payments = {
  count?: number;
  rows?: {
    id?: string;
    userId?: string;
    amount?: string;
    currencyCode?: string;
    subscriptionPlanId?: string;
    paymentMethod?: string;
    paymentStatus?: string;
    status?: string;
    stripeRefId?: any;
    invoiceStripeRefId?: string;
    invoiceNo?: string;
    invoiceLink?: string;
    stripeSubscriptionId?: string;
    stripeCheckoutSessionId?: any;
    metadata?: {
      invoiceId?: string;
      invoicePdfUrl?: string;
      customerEmail?: string;
      customerName?: string;
      billingReason?: string;
      stripeCustomerId?: string;
      invoiceStatus?: string;
      periodStart?: number;
      periodEnd?: number;
      amountDue?: number;
      amountPaid?: number;
      total?: number;
      subtotal?: number;
      invoiceCreated?: number;
      effectiveAt?: number;
    };
    nextBillingDate?: string;
    createdAt?: string;
    updatedAt?: string;
    subscriptionPlan?: {
      planName?: string;
      storage?: string;
      subscriptionPricingPlans?: {
        price?: string;
        currencyCode?: string;
      }[];
    };
    users?: {
      userId?: string;
      fullName?: string;
      email?: string;
    };
  }[];
};
export type StripeSubscriptions = {
  data?: string;
};
export type StripeCreateCheckOut = {
  priceId?: string;
  userId?: string;
};
export type StripeCreateCustomer = {
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  contactNumber?: string;
  address?: {
    line1?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
};
export type CancelSubscriptionResponse = {
  success?: boolean;
  message?: string;
  cancelAt?: string;
};
export type CancelCancellationResponse = {
  success?: boolean;
  message?: string;
};
export type DowngradePlanResponse = {
  success?: boolean;
  message?: string;
  changeType?: string;
  details?: {
    oldPlan?: string;
    newPlan?: string;
    oldPrice?: number;
    newPrice?: number;
    effectiveDate?: string;
  };
};
export type DowngradePlanRequest = {
  newPricingPlanId?: string;
};
export type PlanOptionsResponse = {
  data: {
    currentPlan?: {
      id?: string;
      name?: string;
      price?: number;
      storage?: string;
    };
    upgrades?: {
      pricingPlanId?: string;
      planId?: string;
      planName?: string;
      price?: number;
      currency?: string;
      storage?: string;
    }[];
    downgrades?: any[];
    hasPendingChange?: boolean;
    pendingPlan?: any;
  };
  success?: boolean;
};
export type SubscriptionPlans = {
  count?: number;
  rows?: {
    id?: string;
    planName?: string;
    description?: string;
    features?: string;
    referenceId?: string;
    status?: string;
    order?: number;
    storage?: string;
    storageInGib?: number;
    createdAt?: string;
    updatedAt?: string;
    icon?: string;
    users: {
      fullName?: string;
      email?: string;
    };
    subscriptionPricingPlans?: {
      id?: string;
      subscriptionPlanId?: string;
      price?: string;
      currencyCode?: string;
      planDuration?: number;
      planDurationType?: string;
      planType?: string;
      trialPeriodDays?: any;
      referenceId?: string;
      status?: string;
      createdAt?: string;
      updatedAt?: string;
      isAddOn?: boolean;
    }[];
  }[];
};
export type UserSubscriptionPlan = {
  id?: string;
  subscriptionPlanId?: string;
  status?: string;
  name?: string;
  description?: string;
  storage?: string;
  amount?: string;
  billDate?: string;
};
export type UserSubscriptions = {
  id?: string;
  subscriptionId?: string;
  userId?: string;
  subscriptionPlanId?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
};
export type SubscriptionFeatures = {
  count?: number;
  rows?: {
    id?: string;
    name?: string;
    value?: string;
    status?: string;
    icon?: string;
    subscriptionPlanId?: string;
    createdBy?: any;
    createdAt?: string;
    updatedAt?: string;
  }[];
};
export type GetSubscriptionFeaturesByIdApiResponse =
  /** status 200 SubscriptionFeatures details found. */ {
    ok?: boolean;
    data?: SubscriptionFeatures;
    message?: string;
  };
export type GetSubscriptionFeaturesByIdApiArg = {
  /** ID of the SubscriptionFeatures to retrieve */
  id: string;
};
export const {
  useGetPaymentsQuery,
  useStripePaymentCheckoutMutation,
  useCreateStripeCustomerMutation,
  useCreateBillingPortalSessionMutation,
  useCancelSubscriptionMutation,
  useCancelCancellationMutation,
  useDowngradePlanMutation,
  useCancelScheduledDowngradeMutation,
  useGetPlanChangeOptionsQuery,
  useCalculateUpgradeCostMutation,
  useCreateUpgradeCheckoutMutation,
  useReactivateCanceledSubscriptionMutation,
  useGetSubscriptionFeaturesByIdQuery,
  useGetSubscriptionPlansQuery,
  useGetUserSubscriptionPlansQuery,
  useGetUserSubscriptionsQuery
} = injectedRtkApi;
